import { environment } from '@environment';
import { CacheAreaKey } from 'core/config/cache-config';
import { ClaimHistoryResponse } from 'core/models/red-oxygen/claim-history';
import { ClaimRequest, ResendClaimRequest } from 'core/models/red-oxygen/claim-request';
import { GeneralExperienceRequest, GeneralExperienceResponse } from 'core/models/red-oxygen/general-experience';
import { RedOxygenGetTierInfo, RewardBase } from 'core/models/red-oxygen/get-tier';
import { RedOxygenErrorResponse, RedOxygenGenericRequest, RedOxygenGenericResponse, RedOxygenRequestParams } from 'core/models/red-oxygen/red-oxygen';
import { EndpointHelper } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';
import { StorageHelper } from 'core/services/storage';

const getCacheKey = (url: string, customerNumber: string) => `${url}_${customerNumber}`;

type ClaimResponse = RedOxygenGenericResponse & RewardBase | RedOxygenErrorResponse

export class RedOxygenService {
  private static apiBuilder = new EndpointHelper(`${environment.UTILITY_API_URI}/red-oxygen`);
  private static config = {
    cacheKey: 'req',
    areaKey: CacheAreaKey.Rewards,
  };

  static getTierInfo(customerNumber: string) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam('tier')
      .addRouteParam('getInfo')
      .build();

    const { cacheKey, areaKey } = RedOxygenService.config;
    const innerKey = getCacheKey(url, customerNumber);

    const storage = StorageHelper.GetInstance;
    const storeResponse = storage.getFromCache(cacheKey, innerKey, areaKey);

    if (storeResponse) {
      return Promise.resolve(storeResponse as RedOxygenGetTierInfo);
    }

    const request = {
      customerNumber,
      metaRequest: true
    };

    return HttpClient.GetInstance.post<RedOxygenGenericRequest, RedOxygenGetTierInfo>(url, request)
      .then(response => {
        return storage.setCacheAndReturn(
          cacheKey,
          areaKey,
          response?.data,
          innerKey
        ) as RedOxygenGetTierInfo
      });
  }

  static getGeneralExperience(customerNumber: string): Promise<GeneralExperienceResponse> {
    const url = this.apiBuilder
      .createUrl('experience')
      .addRouteParam('general')
      .build();

    const scriptName = 'MCTRA_GetInfo';
    const gameCode = environment.RED_OXYGEN_GAME_CODE;

    const { cacheKey, areaKey } = RedOxygenService.config;
    const innerKey = getCacheKey(url, customerNumber);

    const storage = StorageHelper.GetInstance;
    const storeResponse = storage.getFromCache(cacheKey, innerKey, areaKey);

    if (storeResponse) {
      return Promise.resolve(storeResponse as GeneralExperienceResponse);
    }

    const request = {
      gameCode,
      scriptName,
      group: false,
      params: {
        customerNumber
      }
    };

    return HttpClient.GetInstance
      .post<GeneralExperienceRequest, GeneralExperienceResponse>(url, request)
      .then(response => {
        return storage.setCacheAndReturn(
          cacheKey,
          areaKey,
          response?.data,
          innerKey
        ) as GeneralExperienceResponse
      });
  }

  static getClaimHistory(customerNumber: string) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam('reward')
      .addRouteParam('claimhistory')
      .build();

    const { cacheKey, areaKey } = RedOxygenService.config;
    const innerKey = getCacheKey(url, customerNumber);

    const storage = StorageHelper.GetInstance;
    const storeResponse = storage.getFromCache(cacheKey, innerKey, areaKey);

    if (storeResponse) {
      return Promise.resolve(storeResponse as ClaimHistoryResponse);
    }

    const request = {
      customerNumber,
      metaRequest: true
    };

    return HttpClient.GetInstance.post<RedOxygenGenericRequest, ClaimHistoryResponse>(url, request)
      .then(response => {
        return storage.setCacheAndReturn(
          cacheKey,
          areaKey,
          response?.data,
          innerKey
        ) as ClaimHistoryResponse
      });
  }

  static claim(request: ClaimRequest) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam('reward')
      .addRouteParam('claim')
      .build();

    return HttpClient.GetInstance.post<ClaimRequest, ClaimResponse>(url, request)
      .then(res => res?.data)
  }

  static optIntoRewards(customerNumber: string) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam('customer')
      .addRouteParam('optin')
      .build();

    return HttpClient.GetInstance.post<RedOxygenRequestParams, RedOxygenErrorResponse>(url, { customerNumber })
      .then(res => res?.data)
  }

  static optOutOfRewards(customerNumber: string) {
    const url = this.apiBuilder.createUrl()
      .addRouteParam('customer')
      .addRouteParam('optout')
      .build();

    return HttpClient.GetInstance.post<RedOxygenRequestParams, RedOxygenErrorResponse>(url, { customerNumber })
      .then(res => res?.data)
  }
}